// src/layouts/DashboardLayout.jsx
import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import { styled, useTheme, alpha } from "@mui/material/styles";
import {
  Box,
  Drawer as MuiDrawer,
  AppBar as MuiAppBar,
  Toolbar,
  List,
  CssBaseline,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
  useMediaQuery,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  // ── Nivel 0 (grupos) ──
  Event as EventIcon,
  People as PeopleIcon,
  Devices as DevicesIcon,
  ReceiptLong as ReceiptLongIcon,
  AccountBalance as AccountBalanceIcon,
  Groups as GroupsIcon,
  Assessment as AssessmentIcon,
  Apps as AppsIcon,
  Build as BuildIcon,
  Inventory as InventoryIcon,
  DirectionsCar as DirectionsCarIcon,
  SimCard as SimCardIcon,
  Router as RouterIcon,
  BatteryChargingFull as BatteryIcon,
  Radio as RadioIcon,
  Settings as SettingsIcon,
  SupportAgent as SupportAgentIcon,
  // ── Nivel 1 (hijos) ──
  Dashboard as DashboardIcon,
  CalendarMonth as CalendarMonthIcon,
  PersonSearch as PersonSearchIcon,
  ContactPhone as ContactPhoneIcon,
  Newspaper as NewspaperIcon,
  AccountTree as AccountTreeIcon,
  Receipt as ReceiptIcon,
  Payments as PaymentsIcon,
  RequestPage as RequestPageIcon,
  History as HistoryIcon,
  ManageAccounts as ManageAccountsIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ListAlt as ListAltIcon,
  Assignment as AssignmentIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  LocationOn as LocationOnIcon,
  Speed as SpeedIcon,
  Sms as SmsIcon,
  SmartToy as SmartToyIcon,
  Notifications as NotificationsIcon,
  // ── Extras para nuevos submenús ──
  StickyNote2 as StickyNote2Icon,
  FolderShared as FolderSharedIcon,
  Send as SendIcon,
  FolderOpen as FolderOpenIcon,
  Task as TaskIcon,
  Book as BookIcon,
  EngineeringOutlined as EngineeringIcon,
  QueryStats as QueryStatsIcon,
  Computer as ComputerIcon,
  ShoppingCart as ShoppingCartIcon,
  PriceCheck as PriceCheckIcon,
  Schedule as ScheduleIcon,
  AppRegistration as AppRegistrationIcon,
  Handyman as HandymanIcon,
  Summarize as SummarizeIcon,
  Wallet as WalletIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  LocalAtm as LocalAtmIcon,
  Construction as ConstructionIcon,
  ApartmentOutlined as ApartmentIcon,
  Person as PersonIcon,
  EventNote as EventNoteIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  HowToReg as HowToRegIcon,
  Storage as StorageIcon,
  Poll as PollIcon,
  Phone as PhoneIcon,
  Quiz as QuizIcon,
  Satellite as SatelliteIcon,
  Public as PublicIcon,
  MyLocation as MyLocationIcon,
  CompareArrows as CompareArrowsIcon,
  Search as SearchIcon,
  Tune as TuneIcon,
  Close as CloseIcon,
  // ── Fallback genérico ──
  Circle as CircleIcon,
} from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import logoTRS from '../assets/logo_portatil3.png';
import MARCA from '../config/marca';

// Contexto global (compact + navStyle)
import { ThemeContext } from "../context/ThemeContext";
// Top navigation responsive (cuando navStyle === "top")
import NavTopResponsive from "../navigation/NavTopResponsive";

// (opcional) Avatar/notificaciones si los usas en sidebar
import UserAvatar from "../components/UserAvatar";
import Notifications from "../components/Notifications";
import GlobalSearchBox from "../components/layout/GlobalSearchBox";
import HelpAssistant from "../components/HelpAssistant";

// Axios central
import axios from "../config/axiosConfig";

const drawerWidth = 240;
const LS_MENU_KEY = "menu:ssr:v2";
const MAX_AGE_MS = 1000 * 60 * 60 * 6; // 6 horas

/* Mixins - NO SE CAMBIAN */
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});
const RAIL_CLOSED_W = 64; // px — ancho del rail colapsado

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: RAIL_CLOSED_W,
  [theme.breakpoints.up("sm")]: { width: RAIL_CLOSED_W },
});

// Altura compartida entre AppBar y DrawerHeader — se pasa como prop
const DrawerHeader = styled("div", {
  shouldForwardProp: (prop) => prop !== "appBarH",
})(({ appBarH = 46 }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: "0 4px",
  minHeight: appBarH,
  height: appBarH,
}));

// AppBar - Se ajusta para que el rail expandido también lo mueva
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "shifted",
})(({ theme, shifted }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  // shift cuando es 'sidebar' o 'rail' abierto (md+)
  ...(shifted && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => {
    const isDark = theme.palette.mode === "dark";
    // Light: gris muy suave (no blanco puro) para diferenciarse del contenido
    // Dark: ligeramente más claro que el fondo
    const drawerBg = isDark
      ? theme.palette.grey[900]
      : theme.palette.grey[50];
    const borderColor = isDark
      ? alpha(theme.palette.common.white, 0.07)
      : alpha(theme.palette.common.black, 0.07);

    const paperStyles = {
      background: drawerBg,
      borderRight: `1px solid ${borderColor}`,
      display: "flex",
      flexDirection: "column",
    };

    return {
      width: drawerWidth,
      flexShrink: 0,
      whiteSpace: "nowrap",
      boxSizing: "border-box",
      ...(open && {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": { ...openedMixin(theme), ...paperStyles, padding: theme.spacing(0.5) },
      }),
      ...(!open && {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": { ...closedMixin(theme), ...paperStyles, padding: theme.spacing(0.5) },
      }),
    };
  }
);

function ElevationOnScroll({ children }) {
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 0 });
  return React.cloneElement(children, { elevation: trigger ? 3 : 0 });
}

/* ───────────────────────────── Helpers de menú ───────────────────────────── */
const ROLES = ["admin", "tecnico", "sistemas", "conta", ""];

const canSee = (userRole, allowed = ROLES) => {
  if (!userRole) return false;
  if (userRole === "admin") return true;
  return Array.isArray(allowed) ? allowed.includes(userRole) : true;
};

const filterByRole = (sections, role) =>
  (sections || [])
    .map((s) => {
      const children = (s.children || []).filter((c) => canSee(role, c.roles));
      const sectionAllowed = canSee(role, s.roles);
      if (!sectionAllowed && children.length === 0) return null;
      return { ...s, children };
    })
    .filter(Boolean);

// Normaliza {label,to,children,icon?} → {text,path,children,icon?}
const normalizeSections = (sections) =>
  (sections || []).map((s) => ({
    text: s.label ?? "",
    path: s.to ?? undefined,
    icon: s.icon ?? null, // si tu API lo envía
    children: normalizeSections(s.children || []),
  }));

const slug = (s = "") =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

// ── Paleta de colores para iconos de nivel 0 ──
// Cada grupo recibe un color fijo independiente del tema
const GROUP_COLORS = {
  agenda:        "#3B82F6", // azul
  cliente:       "#10B981", // verde esmeralda
  equipo:        "#8B5CF6", // violeta
  factura:       "#F59E0B", // ámbar
  contabilidad:  "#EF4444", // rojo
  rrhh:          "#EC4899", // rosa
  reporte:       "#06B6D4", // cian
  plataforma:    "#6366F1", // índigo
  requerim:      "#F97316", // naranja
  vehiculo:      "#84CC16", // lima
  radio:         "#14B8A6", // teal
  modem:         "#A855F7", // púrpura
  chip:          "#64748B", // slate
  bateria:       "#22C55E", // verde
  inventario:    "#F59E0B", // ámbar
  reclamo:       "#EF4444", // rojo
  sms:           "#3B82F6", // azul
  chatbot:       "#8B5CF6", // violeta
  notif:         "#F97316", // naranja
  banco:         "#10B981", // verde
  presupuesto:   "#06B6D4", // cian
  wialon:        "#0EA5E9", // sky
  leasecontrol:  "#0EA5E9", // sky
  flespi:        "#7C3AED", // morado
  sistema:       "#64748B", // slate
  telusgy:       "#0EA5E9", // sky
  administra:    "#475569", // slate oscuro
  default:       "#6366F1", // índigo fallback
};

const getGroupColor = (label = "") => {
  const s = slug(label);
  for (const [key, color] of Object.entries(GROUP_COLORS)) {
    if (s.includes(key)) return color;
  }
  return GROUP_COLORS.default;
};

// ── Iconos nivel 0 — con color ──
const fallbackIconByTitle = (label = "") => {
  const s = slug(label);
  const color = getGroupColor(label);
  const sx = { color, fontSize: "1.15rem" };

  if (s.includes("agenda"))        return <EventIcon sx={sx} />;
  if (s.includes("cliente"))       return <PeopleIcon sx={sx} />;
  if (s.includes("equipo"))        return <DevicesIcon sx={sx} />;
  if (s.includes("factura"))       return <ReceiptLongIcon sx={sx} />;
  if (s.includes("contabilidad"))  return <AccountBalanceIcon sx={sx} />;
  if (s.includes("rr.hh") || s.includes("rrhh") || s.includes("personal")) return <GroupsIcon sx={sx} />;
  if (s.includes("reporte"))       return <AssessmentIcon sx={sx} />;
  if (s.includes("plataforma"))    return <AppsIcon sx={sx} />;
  if (s.includes("requerim"))      return <AssignmentIcon sx={sx} />;
  if (s.includes("vehiculo"))      return <DirectionsCarIcon sx={sx} />;
  if (s.includes("radio"))         return <RadioIcon sx={sx} />;
  if (s.includes("modem"))         return <RouterIcon sx={sx} />;
  if (s.includes("chip") || s.includes("sim")) return <SimCardIcon sx={sx} />;
  if (s.includes("bateria"))       return <BatteryIcon sx={sx} />;
  if (s.includes("inventario") || s.includes("stock")) return <InventoryIcon sx={sx} />;
  if (s.includes("reclamo"))       return <SupportAgentIcon sx={sx} />;
  if (s.includes("sms") || s.includes("mensaje")) return <SmsIcon sx={sx} />;
  if (s.includes("chatbot") || s.includes("chat")) return <SmartToyIcon sx={sx} />;
  if (s.includes("notif"))         return <NotificationsIcon sx={sx} />;
  if (s.includes("configuracion") || s.includes("config")) return <SettingsIcon sx={sx} />;
  if (s.includes("banco"))         return <AccountBalanceIcon sx={sx} />;
  if (s.includes("presupuesto"))   return <RequestPageIcon sx={sx} />;
  if (s.includes("pago"))          return <PaymentsIcon sx={sx} />;
  if (s.includes("wialon") || s.includes("leasecontrol") || s.includes("rastreo") || s.includes("flespi")) return <SatelliteIcon sx={sx} />;
  if (s.includes("sistema"))       return <ComputerIcon sx={sx} />;
  if (s.includes("telusgy"))       return <PhoneIcon sx={sx} />;
  if (s.includes("administra"))    return <ManageAccountsIcon sx={sx} />;
  return <AppsIcon sx={{ ...sx, color: GROUP_COLORS.default }} />;
};

// ── Iconos nivel 1 (hijos) ──
const childIconByLabel = (label = "") => {
  const s = slug(label);

  // Agenda
  if (s.includes("dashboard"))              return <DashboardIcon />;
  if (s.includes("agenda") || s.includes("calendario")) return <CalendarMonthIcon />;
  if (s === "administrar" || (s.includes("administracion") && !s.includes("archivo") && !s.includes("personal"))) return <ManageAccountsIcon />;
  if (s.includes("documento"))              return <FolderOpenIcon />;
  if (s.includes("envio"))                  return <SendIcon />;
  if (s.includes("nota"))                   return <StickyNote2Icon />;
  if (s.includes("proyecto") && !s.includes("modulo")) return <FolderSharedIcon />;
  if (s.includes("solicitud") && !s.includes("permiso") && !s.includes("aprobada")) return <TaskIcon />;

  // Clientes
  if (s.includes("bitacora"))               return <BookIcon />;
  if (s === "ot" || (s.includes("ot") && s.length <= 4)) return <EngineeringIcon />;
  if (s.includes("requerimient") && s.includes("sistema")) return <ComputerIcon />;
  if (s.includes("requerimient"))           return <AssignmentIcon />;

  // Equipos
  if (s.includes("orden") && s.includes("compra")) return <ShoppingCartIcon />;
  if (s.includes("precio"))                 return <PriceCheckIcon />;
  if (s.includes("programacion"))           return <ScheduleIcon />;
  if (s.includes("registrar") && s.includes("modelo")) return <AppRegistrationIcon />;
  if (s.includes("registrar") && s.includes("equipo")) return <HandymanIcon />;

  // Contabilidad / Caja
  if (s.includes("caja") && s.includes("chica")) return <WalletIcon />;
  if (s.includes("caja") && s.includes("instalac")) return <ConstructionIcon />;
  if (s.includes("caja") && s.includes("proyecto")) return <AccountBalanceWalletIcon />;
  if (s.includes("caja") && s.includes("tecnic"))  return <BuildIcon />;
  if (s.includes("caja") && s.includes("viatico"))  return <LocalAtmIcon />;
  if (s.includes("caja"))                   return <WalletIcon />;
  if (s.includes("modulo") && s.includes("proyecto")) return <ApartmentIcon />;

  // RRHH
  if (s.includes("administracion") && s.includes("personal")) return <PersonIcon />;
  if (s.includes("planilla"))               return <EventNoteIcon />;
  if (s.includes("solicitud") && s.includes("aprobada")) return <CheckCircleOutlineIcon />;
  if (s.includes("solicitud") && s.includes("permiso")) return <HowToRegIcon />;
  if (s.includes("solicitudes") && s.includes("permiso")) return <HowToRegIcon />;

  // Sistemas / Archivos
  if (s.includes("administracion") && s.includes("archivo")) return <FolderSharedIcon />;
  if (s.includes("busqueda") && s.includes("servidor")) return <StorageIcon />;
  if (s.includes("invitacion") || s.includes("encuesta")) return <PollIcon />;
  if (s.includes("nomina"))                 return <SummarizeIcon />;
  if (s.includes("produccion"))             return <QueryStatsIcon />;
  if (s.includes("llamada"))                return <PhoneIcon />;
  if (s.includes("cuestionario"))           return <QuizIcon />;
  if (s.includes("reporte") && s.includes("proceso")) return <QueryStatsIcon />;

  // Wialon / Rastreo / Leasecontrol
  if (s.includes("ypfb") && s.includes("nacion")) return <PublicIcon />;
  if (s.includes("ypfb") && s.includes("intern")) return <SatelliteIcon />;
  if (s.includes("trs") && s.includes("rastreo"))  return <MyLocationIcon />;
  if (s.includes("flespi"))                 return <RouterIcon />;
  if (s.includes("comparacion") || s.includes("comparar")) return <CompareArrowsIcon />;
  if (s.includes("busqueda") && s.includes("unidad")) return <SearchIcon />;

  // Genéricos reutilizables
  if (s.includes("cliente") && !s.includes("servicio")) return <PersonSearchIcon />;
  if (s.includes("contacto"))               return <ContactPhoneIcon />;
  if (s.includes("noticia") || s.includes("news")) return <NewspaperIcon />;
  if (s.includes("egreso"))                 return <RemoveCircleOutlineIcon />;
  if (s.includes("factura"))                return <ReceiptIcon />;
  if (s.includes("servicio"))               return <AccountTreeIcon />;
  if (s.includes("extracto") || s.includes("historico")) return <HistoryIcon />;
  if (s.includes("banco"))                  return <AccountBalanceIcon />;
  if (s.includes("pago"))                   return <PaymentsIcon />;
  if (s.includes("usuario"))                return <ManageAccountsIcon />;
  if (s.includes("reporte") || s.includes("reportes")) return <BarChartIcon />;
  if (s.includes("historial"))              return <HistoryIcon />;
  if (s.includes("adicion"))                return <AddCircleOutlineIcon />;
  if (s.includes("devolucion"))             return <RemoveCircleOutlineIcon />;
  if (s.includes("mantenimiento"))          return <BuildIcon />;
  if (s.includes("vehiculo"))               return <DirectionsCarIcon />;
  if (s.includes("placa"))                  return <LocationOnIcon />;
  if (s.includes("indicador"))              return <SpeedIcon />;
  if (s.includes("permiso"))                return <ManageAccountsIcon />;
  if (s.includes("macrocuenta"))            return <PieChartIcon />;
  if (s.includes("presupuesto"))            return <RequestPageIcon />;
  if (s.includes("descripcion"))            return <ListAltIcon />;
  if (s.includes("cheque"))                 return <PaymentsIcon />;
  if (s.includes("plataforma"))             return <AppsIcon />;
  if (s.includes("sms"))                    return <SmsIcon />;
  if (s.includes("reclamo"))                return <SupportAgentIcon />;
  if (s.includes("chatbot") || s.includes("chat")) return <SmartToyIcon />;
  if (s.includes("notif"))                  return <NotificationsIcon />;
  if (s.includes("equipo"))                 return <DevicesIcon />;
  if (s.includes("busqueda") || s.includes("buscar")) return <SearchIcon />;
  if (s.includes("configuracion") || s.includes("config")) return <TuneIcon />;

  // Fallback: punto pequeño neutro
  return <CircleIcon sx={{ fontSize: "6px !important" }} />;
};

// Si en el futuro mandas "icon" como string desde la API
const iconMap = {
  event: <EventIcon />, people: <PeopleIcon />, devices: <DevicesIcon />,
  receipt: <ReceiptLongIcon />, accountbalance: <AccountBalanceIcon />,
  groups: <GroupsIcon />, assessment: <AssessmentIcon />, apps: <AppsIcon />,
};
const resolveIcon = (icon) => {
  if (!icon) return null;
  if (React.isValidElement(icon)) return icon;
  const key = slug(String(icon));
  return iconMap[key] || null;
};

/* ───────────────────────────── Componente ───────────────────────────── */
export default function DashboardLayout({ children }) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const location = useLocation();
  const { navStyle = "sidebar", compact = false } = useContext(ThemeContext);
  const navigate = useNavigate(); 
  const handleNavigation = () => {
    navigate('/dashboard'); 
  };

  const TOP_APPBAR_H = compact ? 45 : 50;
  const TOP_APPBAR_H2 = compact ? 2 : 10;
  const TOP_SUBTAB_H =  0;
  const APPBAR_H = compact ? 38 : 46; // altura compartida AppBar ↔ DrawerHeader

  // Cache keys
  const LS_EXPANDED = "menu:expanded:v1"; // localStorage: expandedMenus

  // Estado del menú
  const [role, setRole] = useState(null);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [sections, setSections] = useState([]);

  // SWR: hidrata desde cache y revalida en segundo plano
  useEffect(() => {
    let mounted = true;

    // 1) Hidrata
     try {
      const raw = localStorage.getItem(LS_MENU_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const isFresh = Date.now() - (parsed.ts || 0) < MAX_AGE_MS;
        if (parsed?.sections && mounted) {
          setRole(parsed.role ?? null);
          setSections(parsed.sections);
          if (isFresh) {
            setLoadingMenu(false);
          }
        }
      }
    } catch {}

    // 2) Revalida
    (async () => {
      try {
        const { data } = await axios.get("/api/menu");
        if (!mounted) return;
        const nextRole = data?.role ?? null;
        const nextSections = Array.isArray(data?.sections) ? data.sections : [];
        setRole(nextRole);
        setSections(nextSections);
        localStorage.setItem(
          LS_MENU_KEY,
          JSON.stringify({ role: nextRole, sections: nextSections, ts: Date.now() })
        );
      } catch (e) {
        // mantener lo cacheado
      } finally {
        if (mounted) setLoadingMenu(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const visibleApiSections = useMemo(() => {
    if (loadingMenu) return [];
    return filterByRole(sections, role);
  }, [sections, role, loadingMenu]);

  const menuStructure = useMemo(() => normalizeSections(visibleApiSections), [visibleApiSections]);

  // ── Buscador del menú ──
  const [menuFilter, setMenuFilter] = useState("");
  const filterActive = menuFilter.trim().length > 0;

  const displayedMenu = useMemo(() => {
    const q = slug(menuFilter.trim());
    if (!q) return menuStructure;
    const filterTree = (nodes) =>
      (nodes || [])
        .map((n) => {
          const kids = filterTree(n.children);
          const selfMatch = slug(n.text).includes(q);
          if (!selfMatch && kids.length === 0) return null;
          // Si coincide el grupo, mostrar todos sus hijos; si no, solo los que coinciden
          return { ...n, children: selfMatch && kids.length === 0 ? n.children : kids };
        })
        .filter(Boolean);
    return filterTree(menuStructure);
  }, [menuStructure, menuFilter]);

  // Lógica de estado del drawer / rail
  const initialOpenState = useMemo(() => {
    if (navStyle === "rail") return false;
    const saved = localStorage.getItem("layout:drawerOpen");
    return saved ? JSON.parse(saved) : false;
  }, [navStyle]);

  const [open, setOpen] = useState(initialOpenState);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isRailLocked, setIsRailLocked] = useState(false);

  // Persistencia del drawer (sidebar)
  useEffect(() => {
    if (navStyle === "sidebar" && isMdUp) {
      localStorage.setItem("layout:drawerOpen", JSON.stringify(open));
    }
  }, [open, isMdUp, navStyle]);

  useEffect(() => {
  const onFocus = () => {
      // si no hay secciones cargadas, intenta otra vez
      if (!sections.length) {
        axios.get("/api/menu").then(/* mismo código */);
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [sections.length]);

  // Compacto → plegar (sidebar)
  useEffect(() => {
    if (navStyle === "sidebar" && isMdUp && compact) setOpen(false);
  }, [compact, isMdUp, navStyle]);

  // Cambiar a rail → forzar cerrado solo cuando CAMBIA navStyle (no en cada open change)
  const prevNavStyleRef = useRef(navStyle);
  useEffect(() => {
    if (prevNavStyleRef.current !== navStyle) {
      prevNavStyleRef.current = navStyle;
      if (navStyle === "rail") {
        setOpen(false);
        setIsRailLocked(false);
      }
    }
  }, [navStyle]);

  const toggleDrawer = (force) => {
    if (navStyle === "rail" && isMdUp) {
      setIsRailLocked((prev) => {
        const next = !prev;
        setOpen(true);
        return next;
      });
      return;
    }
    if (isMdUp) setOpen(typeof force === "boolean" ? force : !open);
    else setMobileOpen(typeof force === "boolean" ? force : !mobileOpen);
  };
  const handleDrawerClose = () => toggleDrawer(false);

  const isMini = navStyle !== "top" && isMdUp && !open;
  const showLabels = !isMini;

  // Acordeón: restaurar desde LS y autoexpand SOLO una vez
  const didAutoExpandRef = useRef(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(LS_EXPANDED);
      if (saved) setExpandedMenus(JSON.parse(saved));
    } catch {/* ignore */}
    // solo al montar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (didAutoExpandRef.current) return; // no repetir en cada ruta
    // Abrir padres de la ruta activa la 1ra vez
    const findActiveParents = (nodes, parentKeys = [], depth = 0, parentKey = "") => {
      let activeKeys = [];
      nodes.forEach((item, idx) => {
        const key = `${parentKey}${item.text}-${depth}-${idx}`;
        const startsWithPath = item.path && location.pathname.startsWith(item.path);
        if (startsWithPath) activeKeys.push(...parentKeys);
        if (Array.isArray(item.children) && item.children.length > 0) {
          activeKeys.push(
            ...findActiveParents(item.children, [...parentKeys, key], depth + 1, `${key}>`)
          );
        }
      });
      return [...new Set(activeKeys)];
    };

    const activeParents = findActiveParents(menuStructure);
    setExpandedMenus((prev) => {
      const init = activeParents.reduce((acc, k) => {
        acc[k] = true;
        return acc;
      }, {});
      return { ...prev, ...init };
    });
    didAutoExpandRef.current = true;
  }, [menuStructure, location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_EXPANDED, JSON.stringify(expandedMenus));
    } catch {/* ignore */}
  }, [expandedMenus]);

  // Toggle acordeón con profundidad
  const toggleMenu = (key, depth) => {
    if (isMini) {
      // En rail cerrado: abrir drawer, bloquearlo y expandir el grupo
      setOpen(true);
      setIsRailLocked(true);
      setExpandedMenus({ [key]: true });
      return;
    }
    setExpandedMenus((prev) => {
      const isOpen = prev[key];
      if (depth === 0) {
        if (isOpen) return {};
        return { [key]: true };
      } else {
        return { ...prev, [key]: !isOpen };
      }
    });
  };

  // ¿La sección top actual tiene subtabs?
  const topHasChildren = useMemo(() => {
    if (navStyle !== "top") return false;
    const starts = (p) => location.pathname === p || location.pathname.startsWith(p + "/");
    const collectLeaves = (arr) =>
      (arr || []).flatMap((it) => (it.path ? [it] : Array.isArray(it.children) ? collectLeaves(it.children) : []));
    for (const grp of menuStructure) {
      const allLeaves = collectLeaves(grp.children);
      if (allLeaves.some((leaf) => starts(leaf.path))) {
        return allLeaves.length > 0;
      }
    }
    return false;
  }, [navStyle, location.pathname, menuStructure]);

  // Render nodos
  const renderNodes = (nodes, depth = 0, parentKey = "") =>
    nodes.map((item, idx) => {
      // En rail cerrado solo mostramos nivel 0 (íconos)
      // Cuando open=true el drawer se expande y los hijos sí se renderizan
      if (isMini && depth > 0) return null;

      const key = `${parentKey}${item.text}-${depth}-${idx}`;
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;
      // Con el buscador activo se expande todo para ver las coincidencias
      const isExpanded = filterActive || Boolean(expandedMenus[key]);

      // Un hijo está activo si la ruta actual empieza con su path
      const childIsActive =
        hasChildren &&
        item.children.some(
          (c) => c.path && location.pathname.startsWith(c.path)
        );
      // Ítem activo: hoja navegable que coincide, o grupo cuyo hijo está activo
      const selected =
        (!hasChildren && !!item.path && location.pathname.startsWith(item.path)) ||
        (hasChildren && childIsActive);

      const handleItemClick = () => {
        if (hasChildren) toggleMenu(key, depth);
      };
      const handleLeafClick = () => {
        if (!isMdUp) setMobileOpen(false);
        // En rail: cerrar y desbloquear al navegar a un hijo
        if (navStyle === "rail" && isMdUp) {
          setIsRailLocked(false);
          setOpen(false);
          setExpandedMenus({});
        }
      };

      const safeKeyId = `collapse-menu-${key.replace(/[^a-zA-Z0-9]/g, "-")}`;

      const nodeIcon =
        depth === 0
          ? resolveIcon(item.icon) || fallbackIconByTitle(item.text)
          : childIconByLabel(item.text);

      return (
        <React.Fragment key={key}>
          <ListItem disablePadding component="li" sx={{ display: "block" }}>
            <Tooltip
              title={isMini ? item.text : ""}
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: (t) => ({
                    bgcolor: t.palette.mode === "dark" ? t.palette.grey[800] : t.palette.grey[900],
                    color: "#fff",
                    fontSize: 11.5,
                    fontWeight: 500,
                    borderRadius: "6px",
                    px: 1.25,
                    py: 0.5,
                  }),
                },
              }}
            >
              <ListItemButton
                onClick={hasChildren ? handleItemClick : undefined}
                component={!hasChildren && item.path ? Link : "div"}
                to={!hasChildren ? item.path : undefined}
                onClickCapture={!hasChildren ? handleLeafClick : undefined}
                dense={compact}
                {...(hasChildren
                  ? {
                      role: "button",
                      "aria-expanded": isExpanded,
                      "aria-controls": safeKeyId,
                    }
                  : {})}
                sx={(t) => {
                  const isDark = t.palette.mode === "dark";

                  // Colores base
                  const hoverBg       = isDark ? alpha("#fff", 0.06)  : t.palette.grey[100];
                  const selectedBg    = isDark ? alpha(t.palette.primary.main, 0.14) : alpha(t.palette.primary.main, 0.07);
                  const expandedBg    = isDark ? alpha("#fff", 0.05)  : "#fff";
                  const iconBase      = alpha(t.palette.text.secondary, 0.5);
                  const iconActive    = t.palette.primary.main;

                  const itemMinH      = compact ? 30 : 36;
                  const isLevel0      = depth === 0;

                  // Botón de grupo expandido: fondo blanco/elevado + sombra sutil (como en la imagen)
                  const groupExpanded = isLevel0 && hasChildren && isExpanded && showLabels;
                  // Botón activo (hoja seleccionada o grupo con hijo activo)
                  const isSelected    = selected;

                  return {
                    borderRadius: isMini ? "10px" : "8px",
                    mx: isMini ? "auto" : 0.5,
                    // En mini: centrado horizontal con margen simétrico
                    ...(isMini ? {
                      width: 44,
                      px: 0,
                      py: 0,
                      minHeight: 44,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      // Pill activo en rail
                      backgroundColor: isSelected
                        ? alpha(t.palette.primary.main, isDark ? 0.2 : 0.1)
                        : "transparent",
                      "&:hover": {
                        backgroundColor: isSelected
                          ? alpha(t.palette.primary.main, isDark ? 0.25 : 0.13)
                          : alpha(isDark ? "#fff" : "#000", 0.06),
                      },
                    } : {
                      my: isLevel0 ? 0.25 : 0.125,
                      minHeight: itemMinH,
                      py: compact ? 0.25 : 0.5,
                      px: isLevel0 ? 1 : 1.25,
                      justifyContent: "flex-start",
                      // Sangría de hijos
                      ...(showLabels && !isLevel0 ? { pl: 2.75 } : {}),
                    }),

                    transition: "background-color 120ms ease, box-shadow 120ms ease",

                    // Estados (solo en modo expandido — mini ya los tiene arriba)
                    ...(!isMini && {
                      // Grupo expandido → card elevado
                      ...(groupExpanded && {
                        backgroundColor: expandedBg,
                        boxShadow: isDark
                          ? "0 1px 4px rgba(0,0,0,0.3)"
                          : "0 1px 4px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)",
                        "&:hover": {
                          backgroundColor: expandedBg,
                          boxShadow: isDark
                            ? "0 1px 4px rgba(0,0,0,0.3)"
                            : "0 1px 4px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)",
                        },
                      }),
                      ...(!groupExpanded && isSelected && {
                        backgroundColor: selectedBg,
                        "&:hover": { backgroundColor: selectedBg },
                      }),
                      ...(!groupExpanded && !isSelected && {
                        "&:hover": { backgroundColor: hoverBg },
                      }),
                      // Barra de acento en el hijo activo
                      ...(!isLevel0 && isSelected && {
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: 9,
                          top: "22%",
                          bottom: "22%",
                          width: 3,
                          borderRadius: 2,
                          backgroundColor: t.palette.primary.main,
                        },
                      }),
                    }),

                    // Ícono
                    "& .MuiListItemIcon-root": {
                      minWidth: 0,
                      mr: isMini ? 0 : (showLabels ? (isLevel0 ? 1.25 : 1) : 0),
                      // Nivel 0: el color viene embebido en el JSX — no pisarlo
                      color: isLevel0 ? "inherit" : (isSelected ? iconActive : iconBase),
                      transition: "color 120ms ease",
                      "& svg": {
                        fontSize: isMini
                          ? "1.3rem"
                          : isLevel0 ? "1.15rem" : compact ? "0.85rem" : "0.95rem",
                      },
                    },

                    // Texto
                    "& .MuiListItemText-primary": {
                      color: isSelected
                        ? t.palette.primary.main
                        : groupExpanded
                          ? t.palette.text.primary
                          : t.palette.text.secondary,
                      transition: "color 120ms ease",
                      fontSize: isLevel0 ? 13 : 12.5,
                      fontWeight: isSelected ? 600 : isLevel0 ? 500 : 400,
                    },

                    // Hover: colorear texto e ícono (nivel 0 conserva su color propio)
                    "&:hover .MuiListItemIcon-root": {
                      color: isLevel0 ? "inherit" : (isSelected ? iconActive : t.palette.text.primary),
                    },
                    "&:hover .MuiListItemText-primary": {
                      color: isSelected ? t.palette.primary.main : t.palette.text.primary,
                    },
                  };
                }}
              >
                {/* Ícono — siempre visible; nivel 0 dentro de un chip de color */}
                <ListItemIcon>
                  {depth === 0 ? (
                    <Box
                      sx={(t) => ({
                        width: isMini ? 32 : 28,
                        height: isMini ? 32 : 28,
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        backgroundColor: alpha(
                          getGroupColor(item.text),
                          t.palette.mode === "dark" ? 0.18 : 0.1
                        ),
                      })}
                    >
                      {nodeIcon}
                    </Box>
                  ) : (
                    nodeIcon
                  )}
                </ListItemIcon>

                {/* Texto */}
                {(showLabels || isMini) && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{ noWrap: true }}
                    sx={{
                      opacity: showLabels ? 1 : 0,
                      transition: "opacity 150ms ease",
                      // Evita que ocupe espacio en mini
                      maxWidth: showLabels ? "unset" : 0,
                      overflow: "hidden",
                      m: 0,
                    }}
                  />
                )}

                {/* Chevron */}
                {hasChildren && (
                  <Box sx={{ opacity: showLabels ? 1 : 0, transition: "opacity 150ms ease", display: "flex", flexShrink: 0 }}>
                    {isExpanded
                      ? <ExpandLessIcon sx={{ fontSize: 15, opacity: 0.45 }} />
                      : <ExpandMoreIcon sx={{ fontSize: 15, opacity: 0.35 }} />
                    }
                  </Box>
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          {/* Hijos con Collapse — ocultos en rail cerrado */}
          {hasChildren && (open || navStyle !== "rail") && (
            <Collapse in={isExpanded && !isMini} timeout={160} unmountOnExit id={safeKeyId}>
              <List
                component="div"
                disablePadding
                dense={compact}
                sx={{ pt: 0.25, pb: 0.5 }}
              >
                {renderNodes(item.children, depth + 1, `${key}>`)}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    });

  // Buscador del menú (oculto en rail colapsado)
  const menuSearchBox = (
    <Box sx={{ px: 1, pt: 0.5, pb: 0.75, display: showLabels ? "block" : "none" }}>
      <TextField
        value={menuFilter}
        onChange={(e) => setMenuFilter(e.target.value)}
        placeholder="Buscar en el menú…"
        size="small"
        margin="none"
        fullWidth
        type="search"
        name="menu-search"
        // Evita que el gestor de contraseñas del navegador confunda este
        // campo con el "usuario" de un formulario de login y lo autocomplete
        // al elegir una credencial guardada en otra parte de la página.
        autoComplete="off"
        inputProps={{ autoComplete: "off", "data-lpignore": "true", "data-1p-ignore": "true" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 16, opacity: 0.5 }} />
            </InputAdornment>
          ),
          endAdornment: menuFilter ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => setMenuFilter("")}
                aria-label="Limpiar búsqueda"
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={(t) => ({
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
            fontSize: 12.5,
            height: 34,
            backgroundColor:
              t.palette.mode === "dark" ? alpha("#fff", 0.04) : "#fff",
          },
        })}
      />
    </Box>
  );

  // Lista del menú + mensaje de "sin resultados"
  const menuList = (
    <>
      {filterActive && displayedMenu.length === 0 && (
        <Typography
          variant="caption"
          sx={{ px: 2, py: 1, display: "block", color: "text.secondary" }}
        >
          Sin resultados para «{menuFilter.trim()}»
        </Typography>
      )}
      <List disablePadding dense={compact} sx={{ px: 0, py: 0.5 }}>
        {renderNodes(displayedMenu)}
      </List>
    </>
  );

  return (
    <Box sx={{ display: "flex", bgcolor: "background.default", minHeight: "100dvh" }}>
      <CssBaseline />

      {/* ====== NAVSTYLE: TOP ====== */}
      {navStyle === "top" && <NavTopResponsive />}

      {/* AppBar (para sidebar/rail) */}
      {navStyle !== "top" && (
        <ElevationOnScroll>
          <AppBar
            position="fixed"
            shifted={(navStyle === "sidebar" || navStyle === "rail") && isMdUp && open}
            sx={{
              bgcolor: "primary.main",
              color: "#fff",
              backgroundImage: "none",
              boxShadow: "none",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Toolbar variant="dense" 
            sx={{
              justifyContent: "space-between",
              minHeight: APPBAR_H,
              px: 1,
              gap: 1,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {(navStyle === "sidebar" || navStyle === "rail") && (
                  
                  <IconButton
                    color="inherit"
                    aria-label="toggle drawer"
                    onClick={() => toggleDrawer()}
                    edge="start"
                    size="small"
                    sx={{ mr: 1, ...(navStyle === "sidebar" && isMdUp && open ? { display: "none" } : {}) }}
                  >
                    <MenuIcon fontSize="small" />
                  </IconButton>
                )}
                <Box 
                  onClick={handleNavigation} // 👈 USO CORRECTO de la función
                  // Estilos para hacerlo interactivo y visualmente como un enlace
                  sx={{
                    cursor: 'pointer', 
                    '&:hover': {
                      color: 'white',
                    },
                    display: 'inline-block', // Para que el hover solo afecte al texto
                  }}
                >
                  <Box
                    onClick={handleNavigation}
                    sx={{ cursor: "pointer", opacity: 0.9, transition: "opacity 120ms", "&:hover": { opacity: 1 } }}
                  >
                    <Box
                      component="img"
                      src={MARCA.logoBlanco || logoTRS}
                      alt={MARCA.nombre ? `${MARCA.nombre} Logo` : "Total Radio Systems Logo"}
                      sx={{ height: MARCA.altoLogoBarra, width: "auto" }}
                    />
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <GlobalSearchBox />
                <Notifications />
                <UserAvatar />
              </Box>
            </Toolbar>
          </AppBar>
        </ElevationOnScroll>
      )}

      {/* ====== NAVSTYLE: SIDEBAR ====== */}
      {navStyle === "sidebar" && (
        <>
          {isMdUp && (
            <Drawer variant="permanent" open={open}>
              <DrawerHeader appBarH={APPBAR_H}>
                <Typography
                  variant="overline"
                  sx={{ mr: "auto", pl: 1.5, fontSize: 10, letterSpacing: 1.2, fontWeight: 700, opacity: 0.55 }}
                >
                  Menú
                </Typography>
                <IconButton onClick={handleDrawerClose} size="small">
                  {theme.direction === "rtl" ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                </IconButton>
              </DrawerHeader>

              {menuSearchBox}

              <Box sx={{ flexGrow: 1, overflowY: "auto", p: 0.25 }}>
                {menuList}
              </Box>

              <Box sx={{ mt: "auto", px: 1, pb: 1, opacity: 0.45 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  © TRS {new Date().getFullYear()}
                </Typography>
              </Box>
            </Drawer>
          )}

          {!isMdUp && (
            <MuiDrawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerClose}
              ModalProps={{ keepMounted: true }}
              // En móvil el drawer va por ENCIMA del AppBar (el AppBar usa drawer+1)
              sx={{ zIndex: (t) => t.zIndex.drawer + 2 }}
              PaperProps={{
                sx: (t) => ({
                  width: drawerWidth,
                  background: t.palette.mode === "dark" ? t.palette.grey[900] : t.palette.grey[50],
                  borderRight: `1px solid ${t.palette.mode === "dark" ? alpha(t.palette.common.white, 0.07) : alpha(t.palette.common.black, 0.07)}`,
                  p: 0.5,
                  display: "flex",
                  flexDirection: "column",
                }),
              }}
            >
              <DrawerHeader appBarH={APPBAR_H}>
                <Typography
                  variant="overline"
                  sx={{ mr: "auto", pl: 1.5, fontSize: 10, letterSpacing: 1.2, fontWeight: 700, opacity: 0.55 }}
                >
                  Menú
                </Typography>
                <IconButton onClick={handleDrawerClose} size="small">
                  {theme.direction === "rtl" ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                </IconButton>
              </DrawerHeader>

              {menuSearchBox}

              <Box sx={{ flexGrow: 1, overflowY: "auto", p: 0.25 }}>
                {menuList}
              </Box>

              <Box sx={{ mt: "auto", px: 1, pb: 1, opacity: 0.45 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  © TRS {new Date().getFullYear()}
                </Typography>
              </Box>
            </MuiDrawer>
          )}
        </>
      )}

      {/* ====== NAVSTYLE: RAIL EXPANDIBLE ====== */}
      {navStyle === "rail" && (
        <>
          {isMdUp && (
            <Drawer
              variant="permanent"
              open={open}
              PaperProps={{
                onMouseEnter: () => setOpen(true),
                onMouseLeave: () => { if (!isRailLocked) setOpen(false); },
              }}
            >
              <DrawerHeader appBarH={APPBAR_H} />
              {menuSearchBox}
              <Box sx={{ flexGrow: 1, overflowY: "auto", p: 0.25 }}>
                {menuList}
              </Box>
              <Box sx={{ mt: "auto", px: 1, pb: 1, opacity: 0.45 }}>
                {open && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                    © TRS {new Date().getFullYear()}
                  </Typography>
                )}
              </Box>
            </Drawer>
          )}

          {!isMdUp && (
            <MuiDrawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerClose}
              ModalProps={{ keepMounted: true }}
              // En móvil el drawer va por ENCIMA del AppBar (el AppBar usa drawer+1)
              sx={{ zIndex: (t) => t.zIndex.drawer + 2 }}
              PaperProps={{
                sx: (t) => ({
                  width: drawerWidth,
                  background: t.palette.mode === "dark" ? t.palette.grey[900] : t.palette.grey[50],
                  borderRight: `1px solid ${t.palette.mode === "dark" ? alpha(t.palette.common.white, 0.07) : alpha(t.palette.common.black, 0.07)}`,
                  p: 0.5,
                  display: "flex",
                  flexDirection: "column",
                }),
              }}
            >
              <DrawerHeader appBarH={APPBAR_H}>
                <Typography
                  variant="overline"
                  sx={{ mr: "auto", pl: 1.5, fontSize: 10, letterSpacing: 1.2, fontWeight: 700, opacity: 0.55 }}
                >
                  Menú
                </Typography>
                <IconButton onClick={handleDrawerClose} size="small">
                  {theme.direction === "rtl" ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                </IconButton>
              </DrawerHeader>

              {menuSearchBox}

              <Box sx={{ flexGrow: 1, overflowY: "auto", p: 0.25 }}>
                {menuList}
              </Box>

              <Box sx={{ mt: "auto", px: 1, pb: 1, opacity: 0.45 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  © TRS {new Date().getFullYear()}
                </Typography>
              </Box>
            </MuiDrawer>
          )}
        </>
      )}

      {/* ====== CONTENIDO ====== */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, md: 1 },
          minWidth: 0,
          ml: navStyle !== "top" && isMdUp ? `calc(${theme.spacing(0)} + 0px)` : 0,
        }}
      >
        {/* Spacer para cada estilo de navegación */}
        {navStyle === "top" && (
          <Box sx={{ height: isMdUp ? TOP_APPBAR_H + (topHasChildren ? TOP_SUBTAB_H : 0) : TOP_APPBAR_H }} />
        )}
        {navStyle === "sidebar" && (
          <Box sx={{ height: isMdUp ? TOP_APPBAR_H2 + (topHasChildren ? TOP_SUBTAB_H : 0) : TOP_APPBAR_H2 }} />
        )}
        {(navStyle === "sidebar" || navStyle === "rail") && <DrawerHeader appBarH={APPBAR_H} />}

        {children}
      </Box>

      <HelpAssistant />
    </Box>
  );
}
